import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';

import { buildNavigationUrl, getAdjacentSlug } from '../utils/navigation';
import { FLOW_SLUGS, isCommonStep, STEP_DEFINITIONS, STEP_SLUG } from '../constants';
import BuildYourCoverActions from '../../../actions/build-your-cover';
import type { JourneyProductType, StepSlug } from '../types';

const useSyncActiveStep = (activeSlug: StepSlug) => {
  const dispatch = useDispatch();

  useEffect(() => {
    BuildYourCoverActions.setActiveStep(activeSlug)(dispatch);
  }, [activeSlug]); // eslint-disable-line react-hooks/exhaustive-deps
};

export type ByocNavigationContextType = {
  activeIndex: number;
  activeSlug: StepSlug;
  activeStep: string;
  analyticsLabel: string;
  goToNextStep: (options?: { journeyType?: JourneyProductType }) => void;
  goToPreviousStep: () => void;
  goToStep: (target: StepSlug) => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isServicesStep: boolean;
  journeyType: JourneyProductType;
  productTypeForOffers: JourneyProductType | null;
  setProductTypeFromForm: (value: JourneyProductType | null) => void;
  steps: string[];
  stepSlugs: StepSlug[];
};

type ByocNavigationProviderProps = {
  activeSlug: StepSlug;
  children: ReactNode;
  journeyType: JourneyProductType;
};

const ByocNavigationContext = createContext<ByocNavigationContextType | undefined>(undefined);

const ByocNavigationProvider = ({ activeSlug, children, journeyType }: ByocNavigationProviderProps) => {
  const router = useRouter();

  useSyncActiveStep(activeSlug);

  const stepSlugs = FLOW_SLUGS[journeyType];
  const activeIndex = stepSlugs.indexOf(activeSlug);
  const steps = stepSlugs.map((slug) => STEP_DEFINITIONS[slug].label);
  const activeStep = STEP_DEFINITIONS[activeSlug].label;
  const { analyticsLabel } = STEP_DEFINITIONS[activeSlug];
  const isFirstStep = activeIndex === 0;
  const isLastStep = activeIndex === stepSlugs.length - 1;
  const isServicesStep = activeSlug === STEP_SLUG.HOSPITAL || activeSlug === STEP_SLUG.EXTRAS;

  // Common steps (about-you) source productType from the form; typed steps source it from the URL.
  const [productTypeFromForm, setProductTypeFromForm] = useState<JourneyProductType | null>(null);
  const productTypeForOffers = isCommonStep(activeSlug) ? productTypeFromForm : journeyType;

  const value = useMemo(
    () => ({
      activeIndex,
      activeSlug,
      activeStep,
      analyticsLabel,
      goToNextStep: (options?: { journeyType?: JourneyProductType }) => {
        const effectiveJourneyType = options?.journeyType ?? journeyType;
        const next = getAdjacentSlug('next', activeSlug, effectiveJourneyType);
        if (!next) return;

        router.push(buildNavigationUrl(next, effectiveJourneyType));
      },
      goToPreviousStep: () => {
        const prev = getAdjacentSlug('prev', activeSlug, journeyType);
        if (!prev) return;

        router.push(buildNavigationUrl(prev, journeyType));
      },
      goToStep: (target: StepSlug) => {
        router.push(buildNavigationUrl(target, journeyType));
      },
      isFirstStep,
      isLastStep,
      isServicesStep,
      journeyType,
      productTypeForOffers,
      setProductTypeFromForm,
      stepSlugs,
      steps,
    }),
    [
      activeIndex,
      activeSlug,
      activeStep,
      analyticsLabel,
      isFirstStep,
      isLastStep,
      isServicesStep,
      journeyType,
      productTypeForOffers,
      router,
      stepSlugs,
      steps,
    ],
  );

  return <ByocNavigationContext.Provider value={value}>{children}</ByocNavigationContext.Provider>;
};

const useByocNavigationContext = (): ByocNavigationContextType => {
  const context = useContext(ByocNavigationContext);
  if (context === undefined) {
    throw new Error('useByocNavigationContext must be used within ByocNavigationProvider');
  }
  return context;
};

export { ByocNavigationProvider, useByocNavigationContext };
